/* eslint-disable no-shadow */
import _ from 'lodash';
import types from './mutation-types';
import { chartTemplate } from '../../../utils/constants';

const state = {
  byId: {},
  allIds: [],
  nextPos: {
    x: 0,
    y: 0,
  },
};

const getters = {
  find: (state) => (id) => state.byId[id],
  list: (state, getters) => state.allIds.map((id) => getters.find(id)),
  count: (state) => state.allIds.length,
};

const mutations = {
  [types.ADD_CHART]: async (state, { chart }) => {
    state.byId[chart.i] = chart;
    if (state.allIds.includes(chart.i)) return;
    state.allIds.push(chart.i);
  },
  [types.REMOVE_CHART]: (state, { uuid }) => {
    const chartIndex = state.allIds.indexOf(uuid);
    if (chartIndex !== -1) {
      delete state.byId[uuid];
      state.allIds.splice(chartIndex, 1);
    }
  },
  [types.UPDATE_CHART]: (state, { uuid, property, value }) => {
    const newChart = {
      ...state.byId[uuid],
      [property]: value,
    };
    state.byId = {
      ...state.byId,
      [uuid]: newChart,
    };
  },
  [types.UPDATE_CHART_QUERY]: (state, { uuid, query }) => {
    state.byId[uuid].query = query;
  },
  [types.SELECT_CHART_AREA]: (state, { uuid, area }) => {
    state.byId[uuid].query.area = area;
  },
  [types.SET_CURRENT_TAB]: (state, { uuid, tabIndex }) => {
    state.byId[uuid].currentDatasetTab = tabIndex;
  },
  [types.CHANGE_PLOT_TAB]: (state, { uuid, tabIndex }) => {
    const newChart = {
      ...state.byId[uuid],
      currentTabIndex: tabIndex,
    };
    state.byId = {
      ...state.byId,
      [uuid]: newChart,
    };
  },
  [types.SET_HAS_DATA]: (state, { uuid, hasData }) => {
    state.byId[uuid].hasData = hasData;
  },
  [types.SET_NEXT_POSITION]: (state, { x, y }) => {
    state.nextPos = {
      ...state.nextPos,
      x,
      y,
    };
  },
};

const actions = {
  /**
   * Action for adding new chart
   */
  addChart: async ({ commit, state }, { chart }) => {
    const newChart = { ...chart };
    newChart.x = state.nextPos.x;
    newChart.y = state.nextPos.y;
    /** Adds chart to the list of charts */
    await commit(types.ADD_CHART, { chart: newChart });

    /**
     * Determine the placement of charts within grid
     * The default width is 6
     * The default height is 14
     */
    const chartsLength = state.allIds.length;
    const newPos = {
      ...state.nextPos,
    };
    if (chartsLength > 0) {
      if (chartsLength % 2 !== 0) {
        newPos.x = 6;
      } else {
        newPos.x = 0;
        if (chartsLength >= 2) {
          newPos.y += 14;
        }
      }
    }

    commit(types.SET_NEXT_POSITION, newPos);
  },
  removeChart: ({ commit }, { uuid }) => {
    // Action for removing a single chart based on uuid
    commit(types.REMOVE_CHART, { uuid });
  },
  updateChartStatus: ({ commit }, { uuid, chartStatus, value }) => {
    // Action to update the instrumentList for trace in InstrumentCards.vue
    // This will be used to control instrumentParams in the future.
    commit(types.UPDATE_CHART, { uuid, property: chartStatus, value });
  },
  updateChartPanelPos: ({ commit }, { uuid, x, y }) => {
    commit(types.UPDATE_CHART, { uuid, property: 'x', value: x });
    commit(types.UPDATE_CHART, { uuid, property: 'y', value: y });
  },
  updateChartPanelSize: ({ commit }, { uuid, w, h }) => {
    commit(types.UPDATE_CHART, { uuid, property: 'w', value: w });
    commit(types.UPDATE_CHART, { uuid, property: 'h', value: h });
  },
  selectChartArea: ({ commit }, { uuid, area }) => {
    commit(types.SELECT_CHART_AREA, { uuid, area });
  },
  updateChartQuery: ({ commit }, { uuid, query }) => {
    commit(types.UPDATE_CHART_QUERY, { uuid, query });
  },
  setCurrentTab: async ({ commit }, { uuid, tabIndex }) => {
    commit(types.SET_CURRENT_TAB, { uuid, tabIndex });
    return tabIndex;
  },
  setHasDataFlag: ({ commit }, { uuid, hasData }) => {
    commit(types.UPDATE_CHART, { uuid, property: 'hasData', value: hasData });
  },
  setChartTab: ({ commit }, { uuid, tabIndex }) => {
    commit(types.CHANGE_PLOT_TAB, { uuid, tabIndex });
  },
  updatePlotLayout: ({ commit, state }, {
    uuid, layout,
  }) => {
    const chartContainer = {
      ...state.byId[uuid],
    };
    const chart = { ...chartContainer.chart };
    chart.layout = layout;
    chart.layout.datarevision = new Date().getTime();
    commit(types.UPDATE_CHART, { uuid, property: 'chart', value: chart });
  },
  resetPlotLayout: ({ dispatch }, { uuid }) => {
    const cleanChart = _.defaultsDeep({}, chartTemplate);
    const layout = { ...cleanChart.chart.layout };
    layout.datarevision = new Date().getTime();

    dispatch('updatePlotLayout', { uuid, layout });
  },
  updatePlotOptions: ({ commit, state }, {
    uuid, options,
  }) => {
    const chartContainer = {
      ...state.byId[uuid],
    };
    const chart = { ...chartContainer.chart };
    chart.options = options;
    commit(types.UPDATE_CHART, { uuid, property: 'chart', value: chart });
  },
  saveLastRequestParams: ({ commit }, { uuid, params }) => {
    commit(types.UPDATE_CHART, { uuid, property: 'lastRequestParams', value: params });
  },
  saveLastJobUUID: ({ commit }, { uuid, jobUUID }) => {
    commit(types.UPDATE_CHART, { uuid, property: 'lastJobUUID', value: jobUUID });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
